import { ChevronDownIcon } from "lucide-react";

import { Column, Row, Text } from "@/components";
import { CallSummarySection } from "./staffer-calls/CallSummarySection";
import { StafferCallWithTranscript } from "./staffer-calls/StafferCallWithTranscript";
import { StatusBadge } from "./staffer-calls/StatusBadge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

import { useSheetScope } from "@/contexts/SheetScopeContext";
import { dates } from "@/lib/dates";
import { cn } from "@/lib/utils";
import type { EnrichmentCandidacy } from "./types";

import { CallResponse } from "@/types";

const formatCallDurationSeconds = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString()}:${secs.toString().padStart(2, "0")}`;
};

export const EnrichmentStafferCallItem = ({
  call,
  candidacy,
  isSelected,
  onToggle,
}: {
  call: CallResponse;
  candidacy: EnrichmentCandidacy;
  isSelected: boolean;
  onToggle: () => void;
}) => {
  /* Contexts */
  const { job } = useSheetScope();

  /* Helpers */
  // Nova types names as non-optional but omits unset fields at runtime.
  const candidateFirstName =
    (candidacy.profile.profile as { firstName?: string }).firstName ?? "";
  const candidateLastName =
    (candidacy.profile.profile as { lastName?: string }).lastName ?? "";
  const stafferName = `${call.user.firstName} ${call.user.lastName}`;
  const formattedDate = dates.formatIsoDateTimeForUser(call.startedAt) ?? "";

  /* Render */
  const render = () => {
    return (
      <Collapsible
        className={cn(
          "group/staffer-call overflow-hidden rounded-lg border bg-white",
          isSelected ? "border-primary-300" : "border-slate-200",
        )}
      >
        <Row className="items-center gap-3 p-3">
          <Checkbox
            aria-label={`Include call from ${formattedDate}`}
            checked={isSelected}
            onCheckedChange={onToggle}
          />
          <CollapsibleTrigger className="flex flex-1 cursor-pointer items-center gap-2 text-left">
            <Text className="font-medium text-slate-700">{formattedDate}</Text>
            <StatusBadge status={call.status} />
            <Text className="text-sm text-slate-500">{stafferName}</Text>
            {call.duration !== null && (
              <Text className="text-xs text-slate-500">
                ({formatCallDurationSeconds(call.duration)})
              </Text>
            )}
            <ChevronDownIcon className="ml-auto h-4 w-4 shrink-0 text-slate-400 transition-transform group-data-[state=closed]/staffer-call:-rotate-90" />
          </CollapsibleTrigger>
        </Row>
        <CollapsibleContent>
          <Column className="gap-4 border-t border-slate-200 p-4">
            {call.audioUrl && call.transcript && (
              <StafferCallWithTranscript
                callId={call.id}
                candidateFirstName={candidateFirstName}
                candidateLastName={candidateLastName}
                jobTitle={job.title}
                transcriptLines={call.transcript.transcriptLines}
              />
            )}
            {call.summary && <CallSummarySection summary={call.summary} />}
          </Column>
        </CollapsibleContent>
      </Collapsible>
    );
  };

  return render();
};
